import type * as Y from "yjs";
import { isBlobNode, isSecretPath, type BlobRef, type TreeNode } from "@ide/shared";
import type { FileHandle, ProjectSession } from "@/collab/session";
import type { WorkspaceContextValue } from "./context";

/**
 * Workspace aberto (sessão, projeto, configuração) para quem roda fora do React: comandos de
 * plugins e o agente de código. Definido pela página do workspace enquanto ela está montada.
 */
export const activeWorkspace: { current: WorkspaceContextValue | null } = { current: null };

export function requireWorkspace(): WorkspaceContextValue {
  if (!activeWorkspace.current) throw new Error("Abra um projeto para usar este comando.");
  return activeWorkspace.current;
}

/**
 * Troca o conteúdo do Y.Text pelo novo com a menor edição possível (prefixo e sufixo comuns
 * ficam). Quem está editando o mesmo arquivo não perde o cursor fora do trecho alterado.
 */
export function applyMinimalEdit(text: Y.Text, next: string): boolean {
  const current = text.toString();
  if (current === next) return false;
  let start = 0;
  const max = Math.min(current.length, next.length);
  while (start < max && current.charCodeAt(start) === next.charCodeAt(start)) start++;
  let endCur = current.length;
  let endNext = next.length;
  while (
    endCur > start &&
    endNext > start &&
    current.charCodeAt(endCur - 1) === next.charCodeAt(endNext - 1)
  ) {
    endCur--;
    endNext--;
  }
  const inserted = next.slice(start, endNext);
  if (inserted.length <= CHUNK_CHARS) {
    text.doc!.transact(() => {
      if (endCur > start) text.delete(start, endCur - start);
      if (inserted) text.insert(start, inserted);
    });
    return true;
  }
  // Texto grande (arquivo de vários MB): em pedaços — cada edição vira uma mensagem para o
  // servidor, e uma mensagem acima de 1 MB derruba a conexão.
  if (endCur > start) text.doc!.transact(() => text.delete(start, endCur - start));
  for (let at = 0; at < inserted.length; at += CHUNK_CHARS) {
    const piece = inserted.slice(at, at + CHUNK_CHARS);
    text.doc!.transact(() => text.insert(start + at, piece));
  }
  return true;
}

/** Até ~200 mil caracteres por edição (no pior caso ~800 KB em UTF-8, abaixo de 1 MB). */
const CHUNK_CHARS = 200_000;

/**
 * Cria de uma vez (uma só mudança na árvore) as pastas e os arquivos que ainda não existem: abrir
 * uma pasta com centenas de arquivos não manda centenas de mensagens para o servidor.
 */
export function createFiles(session: ProjectSession, paths: string[]): void {
  const missing = paths.filter((p) => !findFileId(session, p));
  if (!missing.length) return;
  session.treeDoc.transact(() => {
    for (const path of missing) {
      const parts = path.split("/");
      const name = parts.pop()!;
      try {
        const parent = ensureFolders(session, parts);
        session.createNode(parent, name, "file");
      } catch {
        // Nome inválido ou conflito: a escrita do arquivo explica o erro depois.
      }
    }
  });
}

/** Id do arquivo num caminho, ou null. */
export function findFileId(session: ProjectSession, path: string): string | null {
  for (const [id, p] of session.tree().paths) if (p === path) return id;
  return null;
}

function ensureFolders(session: ProjectSession, folders: string[]): string | null {
  let parent: string | null = null;
  for (const name of folders) {
    const children: TreeNode[] = session.tree().children.get(parent) ?? [];
    const existing = children.find((n) => n.name === name);
    if (existing && existing.type !== "folder") throw new Error(`"${name}" já existe como arquivo.`);
    parent = existing ? existing.id : session.createNode(parent, name, "folder");
  }
  return parent;
}

async function waitSynced(handle: FileHandle, timeoutMs = 5_000) {
  const start = Date.now();
  while (handle.provider.hasUnsyncedChanges && Date.now() - start < timeoutMs) {
    await new Promise((r) => setTimeout(r, 100));
  }
}

/** Lê o conteúdo atual de um arquivo do projeto (abre o documento se preciso). */
export async function readFile(session: ProjectSession, path: string): Promise<string | null> {
  // .env sem a permissão "Ver .env": nem tenta (o servidor recusaria o documento).
  if (activeWorkspace.current?.detail.canSeeEnv === false && isSecretPath(path)) return null;
  const id = findFileId(session, path);
  if (!id) return null;
  // Binário (banco, imagem, zip…): não tem texto para ler.
  if (isBlobNode(session.tree().nodes.get(id))) return null;
  const handle = session.openFile(id);
  try {
    await session.loaded(handle, 8_000);
    return handle.text.toString();
  } finally {
    handle.release();
  }
}

/**
 * Cria (com as pastas) ou atualiza um arquivo pelo caminho. Entra como edição normal da pessoa:
 * aparece no histórico com autoria e chega na hora para quem está no projeto.
 */
export async function writeFile(
  session: ProjectSession,
  path: string,
  content: string,
  /** `confirm`: só conta como feito quando o servidor confirmou (senão, erro — para tentar de novo). */
  options: { confirm?: boolean } = {},
): Promise<"created" | "updated" | "unchanged"> {
  if (!session.canWrite) throw new Error("Seu papel não permite editar arquivos.");
  if (activeWorkspace.current?.detail.canSeeEnv === false && isSecretPath(path))
    throw new Error("Só quem tem a permissão “Ver .env” altera os arquivos .env deste projeto.");
  const parts = path.split("/");
  const name = parts.pop()!;
  let id = findFileId(session, path);
  const created = !id;
  if (!id) {
    const parent = ensureFolders(session, parts);
    id = session.createNode(parent, name, "file");
  }
  // Era binário e agora chega texto: volta a ser arquivo de texto.
  if (isBlobNode(session.tree().nodes.get(id))) session.setFileBlob(id, null);
  const handle = session.openFile(id);
  try {
    // O conteúdo do servidor antes de editar: sobre um cache vazio, a edição duplicaria o texto.
    if (options.confirm) {
      if (!(await session.loadedOk(handle, 30_000))) throw new Error("o servidor não respondeu a tempo");
    } else await session.loaded(handle);
    const changed = applyMinimalEdit(handle.text, content);
    await waitSynced(handle, options.confirm ? 30_000 : 5_000);
    if (options.confirm && handle.provider.hasUnsyncedChanges)
      throw new Error("o servidor não confirmou o envio");
    return created ? "created" : changed ? "updated" : "unchanged";
  } finally {
    handle.release();
  }
}

/** Cria (com as pastas) ou atualiza um arquivo binário: a árvore guarda o sha256 e o tamanho. */
export function writeBlobFile(session: ProjectSession, path: string, blob: BlobRef): void {
  if (!session.canWrite) throw new Error("Seu papel não permite editar arquivos.");
  let id = findFileId(session, path);
  if (!id) {
    const parts = path.split("/");
    const name = parts.pop()!;
    id = session.createNode(ensureFolders(session, parts), name, "file");
  }
  session.setFileBlob(id, blob);
}

export function deleteFile(session: ProjectSession, path: string): boolean {
  const id = findFileId(session, path);
  if (!id) return false;
  session.deleteNode(id);
  return true;
}
